import { Box, InputLabel } from "@mui/material";
import "react-quill-new/dist/quill.snow.css";
import { SxProps, Theme } from "@mui/material/styles";
import { mergeSx } from "src/utils/styles.ts";
import { getLargestActiveBreakpoint, useMediaQueryForAllBreakpointsUp } from "src/utils/breakpointUtils.ts";
import { Breakpoint } from "@mui/system";
import { filterNulls } from "src/utils/arrayUtils.ts";
import React, { Suspense } from "react";
import { LoadingBox } from "src/components/common/LoadingBox.tsx";
import type ReactQuillType from "react-quill-new";
import type { Quill } from "react-quill-new";

type ReactQuillProps = React.ComponentProps<typeof ReactQuillType>;

export interface AavoRichTextEditorProps extends ReactQuillProps {
	label?: string;
	sx?: SxProps<Theme>;
	jasperReportsCompatibility?: boolean;
	toolbarEndAdornment?: React.ReactNode;
}

const ReactQuill = React.lazy(async () => {
	const ret = await import("react-quill-new");
	setupQuill(ret.Quill);
	return ret;
});

export const AavoRichTextEditor = ({
	label,
	sx,
	jasperReportsCompatibility = false,
	toolbarEndAdornment,
	...other
}: AavoRichTextEditorProps) => {
	const breakpoints = useMediaQueryForAllBreakpointsUp();
	const useIfBreakpoint = <T,>(breakpoint: Breakpoint, value: T[]): T[] | null => {
		return breakpoints[breakpoint] ? value : null;
	};

	const useIfNotJasperReportsCompatibility = <T,>(value: T[] | null): T[] | null => {
		return jasperReportsCompatibility ? null : value;
	};

	const breakpointKey = getLargestActiveBreakpoint(breakpoints);
	const hasToolbarEndAdornment = toolbarEndAdornment != null;

	const toolbarOptions = filterNulls([
		["bold", "italic", "underline", "strike"],

		useIfNotJasperReportsCompatibility(["blockquote"]),

		useIfBreakpoint("xs", [{ size: fontSizeArr }, { list: "ordered" }, { list: "bullet" }]),
		useIfNotJasperReportsCompatibility(useIfBreakpoint("md", ["link", "image"])),
		useIfBreakpoint("md", [{ script: "sub" }, { script: "super" }]),
		useIfNotJasperReportsCompatibility(useIfBreakpoint("md", [{ indent: "-1" }, { indent: "+1" }])),

		useIfBreakpoint("sm", [{ color: [] }, { background: [] }]),
		useIfNotJasperReportsCompatibility(
			useIfBreakpoint("md", [{ font: quillFontToolbarValues }]),
		),
		useIfNotJasperReportsCompatibility(useIfBreakpoint("sm", [{ align: [] }])),

		useIfBreakpoint("sm", ["clean"]),
	]);

	return (
		<Suspense fallback={<LoadingBox />}>
			<Box sx={mergeSx(styles, hasToolbarEndAdornment ? toolbarEndAdornmentStyles : undefined, sx)}>
				{label && (
					<InputLabel
						sx={{
							fontSize: "0.75rem",
							margin: 0,
							marginTop: "-1rem",
						}}
					>
						{label}
					</InputLabel>
				)}
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						position: "relative",
						flex: 1,
						minHeight: 0,
					}}
				>
					<ReactQuill
						key={breakpointKey} // Rotating screen causes editor to crash without remount.
						theme="snow"
						style={{
							flex: 1,
							minHeight: 0,
						}}
						modules={{
							toolbar: toolbarOptions,
						}}
						{...other}
					/>
					{hasToolbarEndAdornment && (
						<Box sx={toolbarEndAdornmentWrapperStyles}>{toolbarEndAdornment}</Box>
					)}
				</Box>
			</Box>
		</Suspense>
	);
};

const fontSizeArr = ["8px", "9px", "10px", "12px", "14px", "16px", "20px", "24px", "32px"];

/** Quill 2 does not expand `{ font: [] }`; options must match `attributors/class/font` whitelist. */
const quillFontToolbarValues: Array<false | string> = [false, "serif", "monospace"];

function setupQuill(quill: typeof Quill) {
	const bold = quill.import("formats/bold");
	if (hasTagName(bold)) {
		bold.tagName = "b"; // Quill uses <strong> by default
		quill.register("formats/bold", bold, true);
	}

	const italic = quill.import("formats/italic");
	if (hasTagName(italic)) {
		italic.tagName = "i"; // Quill uses <em> by default
		quill.register("formats/italic", italic, true);
	}

	const strike = quill.import("formats/strike");
	if (hasTagName(strike)) {
		strike.tagName = "strike"; // Use <strike> to support JasperReports
		quill.register("formats/strike", strike, true);
	}

	const sizeStyle = quill.import("attributors/style/size");
	if (hasWhitelist(sizeStyle)) {
		sizeStyle.whitelist = fontSizeArr;
		quill.register("attributors/style/size", sizeStyle, true);
		quill.register("formats/size", sizeStyle, true);
	}
}

type ImportWithTagName = {
	tagName: string | string[];
};

function hasTagName(value: unknown): value is ImportWithTagName {
	return (typeof value === "function" || (typeof value === "object" && value !== null)) && "tagName" in value;
}

type ImportWithWhitelist = {
	whitelist: string[] | undefined;
};

function hasWhitelist(value: unknown): value is ImportWithWhitelist {
	return (typeof value === "function" || (typeof value === "object" && value !== null)) && "whitelist" in value;
}

const styles: SxProps<Theme> = {
	display: "flex",
	flexDirection: "column",
	alignItems: "stretch",
	marginTop: 2,
	minHeight: 0,
	"& .quill": {
		display: "flex",
		flexDirection: "column",
	},
	"& .ql-container": {
		minHeight: 0,
	},
	"& .ql-editor": {
		overflow: "auto",
	},
	"& .ql-toolbar": {
		minHeight: "unset",
	},
	...fontSizeArr.reduce(
		(acc, size) => ({
			...acc,
			// [`.ql-picker.ql-size .ql-picker-item[data-value="${size}"]::before`]: {
			[`& .ql-picker.ql-size .ql-picker-item[data-value="${size}"]::before`]: {
				content: `"${size}"`,
				fontSize: size,
			},
			[`& .ql-picker.ql-size .ql-picker-label[data-value="${size}"]::before`]: {
				content: `"${size}"`,
			},
		}),
		{},
	),
};

const toolbarEndAdornmentStyles: SxProps<Theme> = {
	"& .ql-toolbar": {
		paddingRight: {
			xs: "10rem",
			sm: "13rem",
		},
	},
};

const toolbarEndAdornmentWrapperStyles: SxProps<Theme> = {
	alignItems: "center",
	display: "flex",
	justifyContent: "flex-end",
	minHeight: 42,
	maxWidth: {
		xs: "8.5rem",
		sm: "11.5rem",
	},
	overflow: "hidden",
	pointerEvents: "none",
	position: "absolute",
	right: 12,
	top: 0,
	zIndex: 1,
	"& > *": {
		pointerEvents: "auto",
	},
};
